/**
 * Byte writer and reader for the wire format. Little-endian fixed widths,
 * LEB128 varints (unsigned, and zigzag for signed) exact up to 2^53, and
 * length-prefixed UTF-8 strings with a fast path for short ASCII (ids).
 */

// Typed by hand so this builds with or without the DOM / Node type libs.
interface Utf8Encoder {
  encode(s: string): Uint8Array;
}
interface Utf8Decoder {
  decode(b: Uint8Array): string;
}
const g = globalThis as unknown as { TextEncoder: new () => Utf8Encoder; TextDecoder: new () => Utf8Decoder };
const utf8Encoder = new g.TextEncoder();
const utf8Decoder = new g.TextDecoder();

/** Strings up to this long are scanned for a pure-ASCII fast path before falling back to TextEncoder. */
const ASCII_FAST = 64;

export class Writer {
  private buf: Uint8Array;
  private view: DataView;
  pos = 0;

  constructor(size = 1024) {
    this.buf = new Uint8Array(size);
    this.view = new DataView(this.buf.buffer);
  }

  reset(): void {
    this.pos = 0;
  }

  /** A copy of everything written so far. */
  finish(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }

  /** A copy of the bytes written between two positions. */
  slice(from: number, to = this.pos): Uint8Array {
    return this.buf.slice(from, to);
  }

  private need(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.pos + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(next.buffer);
  }

  u8(v: number): void {
    this.need(1);
    this.buf[this.pos++] = v;
  }

  i8(v: number): void {
    this.need(1);
    this.view.setInt8(this.pos++, v);
  }

  u16(v: number): void {
    this.need(2);
    this.view.setUint16(this.pos, v, true);
    this.pos += 2;
  }

  i16(v: number): void {
    this.need(2);
    this.view.setInt16(this.pos, v, true);
    this.pos += 2;
  }

  u32(v: number): void {
    this.need(4);
    this.view.setUint32(this.pos, v, true);
    this.pos += 4;
  }

  i32(v: number): void {
    this.need(4);
    this.view.setInt32(this.pos, v, true);
    this.pos += 4;
  }

  f32(v: number): void {
    this.need(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
  }

  f64(v: number): void {
    this.need(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
  }

  /** Unsigned varint: a non-negative integer up to 2^53, 7 bits a byte. */
  uvar(v: number): void {
    this.need(8);
    if (v < 0x80000000) {
      while (v >= 0x80) {
        this.buf[this.pos++] = (v & 0x7f) | 0x80;
        v >>>= 7;
      }
    } else {
      while (v >= 0x80) {
        this.buf[this.pos++] = (v % 0x80) | 0x80;
        v = Math.floor(v / 0x80);
      }
    }
    this.buf[this.pos++] = v;
  }

  /** Signed varint (zigzag): small magnitudes of either sign take one byte. */
  svar(v: number): void {
    this.uvar(v >= 0 ? v * 2 : -v * 2 - 1);
  }

  str(s: string): void {
    const n = s.length;
    if (n <= ASCII_FAST) {
      let ascii = true;
      for (let i = 0; i < n; i++) {
        if (s.charCodeAt(i) >= 0x80) {
          ascii = false;
          break;
        }
      }
      if (ascii) {
        this.uvar(n);
        this.need(n);
        for (let i = 0; i < n; i++) this.buf[this.pos++] = s.charCodeAt(i);
        return;
      }
    }
    const bytes = utf8Encoder.encode(s);
    this.uvar(bytes.length);
    this.bytes(bytes);
  }

  bytes(b: Uint8Array): void {
    this.need(b.length);
    this.buf.set(b, this.pos);
    this.pos += b.length;
  }
}

export class Reader {
  private readonly view: DataView;
  pos = 0;

  constructor(private readonly buf: Uint8Array) {
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  get eof(): boolean {
    return this.pos >= this.buf.length;
  }

  u8(): number {
    return this.buf[this.pos++]!;
  }

  i8(): number {
    return this.view.getInt8(this.pos++);
  }

  u16(): number {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  i16(): number {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  i32(): number {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f32(): number {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f64(): number {
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  uvar(): number {
    let v = 0;
    let shift = 1;
    for (;;) {
      const b = this.buf[this.pos++];
      if (b === undefined) throw new RangeError("netsync: read past the end");
      v += (b & 0x7f) * shift;
      if (b < 0x80) return v;
      shift *= 0x80;
    }
  }

  svar(): number {
    const n = this.uvar();
    return n % 2 === 0 ? n / 2 : -(n + 1) / 2;
  }

  str(): string {
    const n = this.uvar();
    const end = this.pos + n;
    if (end > this.buf.length) throw new RangeError("netsync: read past the end");
    if (n <= ASCII_FAST) {
      let s = "";
      let i = this.pos;
      for (; i < end; i++) {
        const b = this.buf[i]!;
        if (b >= 0x80) break;
        s += String.fromCharCode(b);
      }
      if (i === end) {
        this.pos = end;
        return s;
      }
    }
    const s = utf8Decoder.decode(this.buf.subarray(this.pos, end));
    this.pos = end;
    return s;
  }
}
